import React, { useState, memo, useMemo, useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getIndicators, singleValue, orderStatus } from "../../../utils";
import { validPog } from "../../../utils/pogValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import InlinePOGLoader from "./InlinePOGLoader";

const CustomPOGWrapperSx = (
  pogBlocked,
  isSelected,
  isReleased,
  isExisting,
  isDuplicateSelection,
  virtualLoading = true,
  isFillIn = false
) => ({
  marginBottom: "1px",
  backgroundColor: pogBlocked || isDuplicateSelection ? "#00000014" : "white",
  overflow: "none",
  boxShadow: !virtualLoading
    ? getIndicators(
        false,
        false,
        isSelected,
        isReleased,
        isExisting,
        isDuplicateSelection,
        isFillIn
      )
    : "",
});

const LoadingPOGContainerSx = {
  display: "flex",
  minHeight: "38px",
  alignItems: "center",
  paddingLeft: "5px",
  fontSize: "12px",
};

const CustomPOGContainerSx = {
  display: "flex",
};

const CustomPOGExpandableContainerSx = {
  ...CustomPOGContainerSx,
  marginBottom: "3px",
};

function CustomPOG({
  pog,
  expandedView,
  isDuplicateSelection,
  collapsedView,
  onChange,
}) {
  const [expanded, setExpanded] = useState(false);

  const isItemsDepartment = isItemDepartment(pog);

  useEffect(() => {
    if (pog?.virtualLoading) {
      setExpanded(false);
    }
  }, [pog?.virtualLoading]);

  useEffect(() => {
    onChange && onChange();
  }, [expanded, onChange]);

  const handleExpand = (e) => {
    setExpanded(!expanded);
  };

  const virtualLoading = pog?.virtualLoading;

  // Auto expand when department is X as there is always only one POG present.
  useEffect(() => {
    if (!virtualLoading && isItemsDepartment) {
      setExpanded(true);
    }
  }, [virtualLoading, isItemsDepartment]);

  const isSelected = useMemo(
    () => pog?.Selected === singleValue.yes && !virtualLoading,
    [pog?.Selected, virtualLoading]
  );

  const isReleased = useMemo(
    () => pog?.ReleaseStatus === orderStatus.released && !virtualLoading,
    [pog?.ReleaseStatus, virtualLoading]
  );

  const isExisting = useMemo(
    () => pog?.Selected === singleValue.existing && !virtualLoading,
    [pog?.Selected, virtualLoading]
  );

  const pogBlocked = useMemo(
    () => !validPog(pog) && !virtualLoading,
    [pog, virtualLoading],
    virtualLoading
  );

  const isFillIn = useMemo(
    () => Boolean(pog?.AllItemsFromFillIn),
    [pog?.AllItemsFromFillIn]
  );

  return (
    <div
      style={CustomPOGWrapperSx(
        pogBlocked,
        isSelected,
        isReleased,
        isExisting,
        isDuplicateSelection,
        virtualLoading,
        isFillIn
      )}
    >
      {virtualLoading ? (
        <div style={LoadingPOGContainerSx}>
          <InlinePOGLoader />
        </div>
      ) : (
        <>
          <div style={CustomPOGContainerSx}>
            {collapsedView}
            <IconButton
              size="small"
              aria-label="expand"
              component="label"
              sx={{ display: "inline-flex" }}
              onClick={handleExpand}
            >
              {!expanded ? (
                <KeyboardArrowRightIcon fontSize="inherit" />
              ) : (
                <KeyboardArrowDownIcon fontSize="inherit" />
              )}
            </IconButton>
          </div>
          <div style={CustomPOGExpandableContainerSx}>
            {expanded && <>{expandedView}</>}
          </div>
        </>
      )}
    </div>
  );
}

export default memo(CustomPOG);
